import {
  type GridApiPro,
  type GridColDef,
  type GridColumnResizeParams,
  type GridColumnVisibilityModel,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { type RefObject } from 'react';
import { z } from 'zod';

import { BrowserStorageVariant, useBrowserStorage } from '../../browserStorage/useBrowserStorage';
import { isDefined } from '../../utils/types';

const EMPTY_VISIBILITY_MODEL: GridColumnVisibilityModel = {};

const columnDimensionsSchema = z.object({
  width: z.number().optional(),
});

const persistedColumnsSchema = z.object({
  columnVisibilityModel: z.record(z.string(), z.boolean()).optional(),
  orderedFields: z.array(z.string()).optional(),
  dimensions: z.record(z.string(), columnDimensionsSchema).optional(),
});

type PersistedColumnsState = z.infer<typeof persistedColumnsSchema>;

interface UsePersistedColumnsProps<TRow extends GridValidRowModel> {
  tableId: string | undefined;
  apiRef: RefObject<GridApiPro | null>;
  columns: readonly GridColDef<TRow>[];
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
}

/**
 * Persists a table's column visibility, order, and width to localStorage (per table),
 * re-applying it via the `columns` prop and a controlled visibility model. Disabled when `tableId`
 * is omitted. Seed default-hidden columns via `initialColumnVisibilityModel` since visibility
 * becomes controlled when enabled.
 */
export function usePersistedColumns<TRow extends GridValidRowModel>(
  props: UsePersistedColumnsProps<TRow>
) {
  const { tableId, apiRef, columns, initialColumnVisibilityModel } = props;
  const isEnabled = isDefined(tableId);

  const { storedValues: storedItem, setStoredValues: setStoredItem } =
    useBrowserStorage<PersistedColumnsState>({
      // `Staff:` prefix is kept verbatim so staff users do not lose their saved column layout.
      storageKeyName: `Staff:TableColumns:${tableId ?? '__disabled__'}`,
      variant: BrowserStorageVariant.LocalStorage,
      schema: persistedColumnsSchema,
      initialValue: {},
    });

  if (!isEnabled) {
    return {
      columns,
      columnVisibilityModel: undefined,
      onColumnOrderChange: undefined,
      onColumnWidthChange: undefined,
      onColumnVisibilityModelChange: undefined,
    };
  }

  const orderedFields = storedItem?.orderedFields;
  const dimensions = storedItem?.dimensions;

  let hydratedColumns: readonly GridColDef<TRow>[] = columns;
  if (isDefined(dimensions) && Object.keys(dimensions).length > 0) {
    hydratedColumns = columns.map((column) => {
      const width = dimensions[column.field]?.width;
      return isDefined(width) ? { ...column, width, flex: undefined } : column;
    });
  }
  if (isDefined(orderedFields) && orderedFields.length > 0) {
    const orderIndexByField = new Map(orderedFields.map((field, index) => [field, index]));
    hydratedColumns = [...hydratedColumns].sort(
      (first, second) =>
        (orderIndexByField.get(first.field) ?? Number.MAX_SAFE_INTEGER) -
        (orderIndexByField.get(second.field) ?? Number.MAX_SAFE_INTEGER)
    );
  }

  return {
    columns: hydratedColumns,
    columnVisibilityModel:
      storedItem?.columnVisibilityModel ?? initialColumnVisibilityModel ?? EMPTY_VISIBILITY_MODEL,
    onColumnOrderChange: () => {
      setStoredItem({
        ...storedItem,
        orderedFields: apiRef.current?.exportState().columns?.orderedFields,
      });
    },
    onColumnWidthChange: (params: GridColumnResizeParams) => {
      setStoredItem({
        ...storedItem,
        dimensions: {
          ...storedItem?.dimensions,
          [params.colDef.field]: { width: params.width },
        },
      });
    },
    onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => {
      setStoredItem({ ...storedItem, columnVisibilityModel: model });
    },
  };
}
